import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Paper,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { AppDispatch } from '../../store';
import { tripDraftUtils, TripDraft } from '../../Utils/tripDraftStorage';

const TripDrafts: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const tripDrafts = useSelector((state: RootState) => state.tripDraft?.drafts || []);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState<string | null>(null);

  const handleEditDraft = (draft: TripDraft) => {
    navigate('/createtrip', { state: { draft, isEdit: true } });
  };

  const handleViewDraft = (draft: TripDraft) => {
    navigate('/createtrip', { state: { draft, isPreview: true } });
  };

  const handleDeleteClick = (draftId: string) => {
    setDraftToDelete(draftId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (draftToDelete) {
      try {
        tripDraftUtils.deleteDraft(dispatch, draftToDelete);
        setDeleteDialogOpen(false);
        setDraftToDelete(null);
      } catch (error) {
        console.error('Error deleting draft:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDraftToDelete(null);
  };

  const handleClearAllDrafts = () => {
    if (window.confirm('Are you sure you want to delete all drafts?')) {
      tripDraftUtils.clearAllDrafts(dispatch);
    }
  };

  if (tripDrafts.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          No trip drafts found. Start creating a trip and save it as draft!
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/createtrip')}
          sx={{
            backgroundColor: '#F57C00',
            '&:hover': { backgroundColor: '#E65100' },
          }}
        >
          Create New Trip
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Trip Drafts ({tripDrafts.length})
        </Typography>
        <Button
          variant="outlined"
          color="error"
          onClick={handleClearAllDrafts}
          sx={{ mb: 2 }}
        >
          Clear All Drafts
        </Button>
      </Box>

      <Stack spacing={2}>
        {tripDrafts.map((draft) => (
          <Card key={draft.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {draft.title}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleViewDraft(draft)}
                    sx={{ color: '#F57C00' }}
                  >
                    <ViewIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleEditDraft(draft)}
                    sx={{ color: '#1976d2' }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDeleteClick(draft.id)}
                    sx={{ color: '#d32f2f' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Draft</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this draft? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TripDrafts;
